import { Box, Divider, Stack, Typography } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { ReviewMode } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { DateTime } from "luxon";
import React, { useState, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

import { ANY_ENCAMP_STAFF_REVIEWER, ANY_ENCAMP_STAFF_REVIEWER_ID } from "./peerReviewQueue";
import { PriorityOrderSwitch } from "./PriorityOrderSwitch";
import { ReviewersEditList, ReviewerPicker, Reviewer } from "./ReviewersEditList";

gql(`
fragment SubmissionReviewerPickerUser on User {
    id
    email
    person {
      first
      last
    }
  }
`);

gql(`
  fragment SubmissionReviewerPicker on SubmissionReviewer {
    id
    dueDate
    reviewOrder
    isStaff
    user {
      ...SubmissionReviewerPickerUser
    }
  }
`);

export interface SubmissionReviewerFormData {
  userId: string;
  reviewOrder: number;
  isRequired: boolean;
  isSigner: boolean;
  dueDate: DateTime;
}

interface ReviewerManagementDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (reviewers: Reviewer[], reviewMode: ReviewMode, peerReviewRequired?: boolean) => void;
  onChange?: (reviewers: Reviewer[], reviewMode: ReviewMode) => void;
  initialReviewers?: Reviewer[];
  initialReviewMode?: ReviewMode;
  /**
   * Add "Any Encamp Staff member" (the open peer-review queue) as the first
   * option in the staff "Peer Reviewer" picker — selecting it requires review
   * from any Encamp staff rather than a specific person. The 8700-12 stepper
   * opts into this; the legacy submission form leaves it off and the picker lists
   * only specific staff. When on, `onSave` receives the peer-review flag as its
   * third argument.
   */
  showEncampStaffPeerReview?: boolean;
  initialPeerReviewRequired?: boolean;
  submissionFacilityIds?: string[];
}

export function ReviewerManagementDialog({
  open,
  onClose,
  onSave,
  onChange,
  initialReviewers = [],
  initialReviewMode = ReviewMode.Parallel,
  showEncampStaffPeerReview = false,
  initialPeerReviewRequired = false,
  submissionFacilityIds = [],
}: ReviewerManagementDialogProps) {
  // Store initial snapshot for cancel/revert
  const initialSnapshotRef = useRef({
    reviewers: initialReviewers,
    reviewMode: initialReviewMode,
  });

  const [reviewers, setReviewers] = useState<Reviewer[]>(() => {
    // Filter out staff reviewers from regular reviewers
    const nonStaffReviewers = initialReviewers.filter((r) => !r.isStaff);
    return nonStaffReviewers.length > 0
      ? nonStaffReviewers
      : [{ id: uuidv4(), name: "", email: "", order: 1, isStaff: false }];
  });
  const [staffReviewer, setStaffReviewer] = useState<Reviewer | null>(
    initialReviewers.find((r) => r.isStaff) ?? null
  );
  // Open-queue peer review (stepper only): "any Encamp staff can review".
  const [peerReviewEnabled, setPeerReviewEnabled] = useState(initialPeerReviewRequired);

  const [reviewMode, setReviewMode] = useState(initialReviewMode);

  const { isStaff } = useCurrentUser();

  // The single staff "Peer Reviewer" picker drives two mutually-exclusive
  // outcomes: a specific staff person (a real reviewer row) or "Any Encamp Staff
  // member" (the open peer-review queue → peerReviewRequired). Selecting one
  // clears the other.
  const peerReviewerValue = staffReviewer ?? (peerReviewEnabled ? ANY_ENCAMP_STAFF_REVIEWER : null);
  const handlePeerReviewerChange = (reviewer: Reviewer | null) => {
    if (reviewer?.id === ANY_ENCAMP_STAFF_REVIEWER_ID) {
      setStaffReviewer(null);
      setPeerReviewEnabled(true);
      return;
    }
    setStaffReviewer(reviewer);
    setPeerReviewEnabled(false);
  };

  // Helper to build valid reviewers without mutating
  const buildValidReviewers = (
    currentReviewers: Reviewer[],
    currentStaffReviewer: Reviewer | null
  ): Reviewer[] => {
    let validReviewers = currentReviewers.filter((r) => r.name.trim());

    // Renumber without mutation
    validReviewers = validReviewers.map((r, index) => ({
      ...r,
      order: index + 1,
    }));

    // Add staff reviewer if valid
    if (currentStaffReviewer && currentStaffReviewer.email.trim()) {
      const staffWithOrder = { ...currentStaffReviewer, order: 0 };
      validReviewers = [staffWithOrder, ...validReviewers];
    }

    return validReviewers;
  };

  // Notify parent when changes happen - using ref to avoid adding to deps
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!onChangeRef.current) return;

    const validReviewers = buildValidReviewers(reviewers, staffReviewer);
    onChangeRef.current(validReviewers, reviewMode);
  }, [reviewers, staffReviewer, reviewMode]);

  const handleCancel = () => {
    // Revert to initial snapshot
    if (onChange) {
      onChange(initialSnapshotRef.current.reviewers, initialSnapshotRef.current.reviewMode);
    }
    onClose();
  };

  const handleSave = () => {
    // Validate that all reviewers have name
    let validReviewers = reviewers.filter((r) => r.name.trim());

    // non staff reviewers will have order of 1, 2, 3, etc.
    // and we want to condense the indexes down and skip any blank reviewers
    validReviewers.forEach((r, index) => {
      r.order = index + 1;
    });

    // staff reviewers will have order of 0, always first
    if (staffReviewer && staffReviewer.email.trim()) {
      staffReviewer.order = 0;
      validReviewers = [staffReviewer, ...validReviewers];
    }

    // With the Encamp-staff peer-review queue on, a config of "peer review only,
    // no named reviewers" is valid, so don't block an otherwise-empty save.
    if (validReviewers.length === 0 && !(showEncampStaffPeerReview && peerReviewEnabled)) {
      return;
    }

    onSave(validReviewers, reviewMode, showEncampStaffPeerReview ? peerReviewEnabled : undefined);
  };

  const canSave =
    reviewers.some((r) => r.name.trim()) ||
    !!(staffReviewer && staffReviewer.email.trim()) ||
    (showEncampStaffPeerReview && peerReviewEnabled);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        title="Add Reviewer"
        description={
          <>
            Add one or more reviewers with the option to assign an order. These reviewers will be
            emailed the submission to sign off before handing to the next reviewer and lastly, the
            signer. If no priority order is selected, all reviewers will be emailed simultaneously.
          </>
        }
        primaryActionButtonProps={{
          saveText: "SAVE",
          onClick: handleSave,
          disabled: !canSave,
        }}
        cancelButtonProps={{
          text: "CANCEL",
          onClick: handleCancel,
        }}
      >
        <Stack spacing={3}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <PriorityOrderSwitch value={reviewMode} onChange={setReviewMode} labelSize="16px" />
          </Box>
          {isStaff && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body1" width="163px" alignContent="center">
                  Peer Reviewer
                </Typography>
                <ReviewerPicker
                  staffOnly={true}
                  // Stepper only: the first option is "Any Encamp Staff member"
                  // (the open peer-review queue), ahead of specific staff users.
                  includeAnyStaffOption={showEncampStaffPeerReview}
                  onChange={handlePeerReviewerChange}
                  value={peerReviewerValue}
                />
              </Box>
              {showEncampStaffPeerReview && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Choose a specific Encamp staff reviewer, or “Any Encamp Staff member” to leave it
                  open for any staff member to claim.
                </Typography>
              )}
              <Divider sx={{ mt: 3 }} />
            </Box>
          )}

          {/* Reviewers List */}
          <ReviewersEditList
            reviewers={reviewers.filter((r) => !r.isStaff)}
            setReviewers={setReviewers}
            rowReordering={reviewMode === ReviewMode.Sequential}
            submissionFacilityIds={submissionFacilityIds}
          />
        </Stack>
      </Dialog>
    </LocalizationProvider>
  );
}
